<template lang="pug">

.groups-page
  .content-container
    v-card.groups-card
      v-card-title.groups-title {{ isSchool ? 'Классы' : 'Группы' }}

      v-card-text.pa-6
        v-row.groups-row(:class="colClasses")
          v-col(
            v-for="col, i in cols"
            :key="i"
            align-self="start"
          )
            .groups-column
              .letter {{ getKey(col[0].name) }}

              router-link.group-link(
                v-for="group in col"
                :key="group.id"
                :to="{ name: 'groups_lessons', params: { groupId: group.id }}"
              ) {{ group.name }}

</template>

<script>
import { mapGetters, mapState } from 'vuex'
import store from '@/store'

export default {
  name: 'Groups',

  async beforeRouteEnter(to, from, next) {
    await store.dispatch('dictionaries/fetchGroups')

    store.commit('breadcrumbs/update', [{ text: store.getters['bootstrap/isSchool'] ? 'Классы' : 'Группы' }])

    next()
  },

  computed: {
    ...mapState('dictionaries', ['groups']),
    ...mapGetters('bootstrap', ['isSchool']),

    cols() {
      const result = []
      let lastArr = []

      for (const group of this.groups) {
        const lastName = lastArr[lastArr.length - 1]?.name || ''

        if (
          lastName[0] !== group.name[0] ||
          this.getKey(lastName) !== this.getKey(group.name)
        ) {
          if (lastArr.length) result.push(lastArr)

          lastArr = [group]
        } else {
          lastArr.push(group)
        }
      }

      if (lastArr.length) result.push(lastArr)

      return result
    },

    // Определяем оптимальную ширину колонок (максимум 6 столбцов)
    colClasses() {
      const colsCount = this.cols.length

      // Проверяем есть ли длинные названия групп
      const hasLongNames = this.groups.some(group => group.name.length > 8)

      if (hasLongNames && colsCount <= 4) return 'mobile-single-col'  // Длинные названия - 1 колонка на мобилках
      if (colsCount <= 3) return 'wide-cols'                          // Мало групп - широкие колонки (4 колонки)
      return 'medium-cols'                                             // Много групп - максимум 6 колонок
    }
  },

  methods: {
    getKey(str) {
      if (!str?.trim()) return null

      if (/^[0-9]+/.test(str)) return str.match(/^[0-9]+/)[0]

      return str.trim().split(/\s+|[-_]/)[0]
    },
  },
}
</script>

<style lang="sass" scoped>

.groups-page
  padding-bottom: 40px

.content-container
  max-width: 1200px
  margin: 0 auto
  padding: 0 24px

.groups-card
  background: white
  box-shadow: 0 2px 8px rgba(0,0,0,0.1)
  border: 1px solid #e2e8f0
  margin-top: 20px

.groups-title
  color: #1e293b
  font-size: 1.5rem
  font-weight: 600
  padding: 24px 24px 0
  border-bottom: 1px solid #f1f5f9

.groups-row
  margin: 0

.groups-column
  display: flex
  flex-direction: column

// Адаптивная ширина колонок в зависимости от количества групп
.wide-cols .col
  flex: 0 0 25%
  max-width: 25%
  padding: 0 16px

.medium-cols .col
  flex: 0 0 16.66%
  max-width: 16.66%
  padding: 0 14px

.col
  flex: 0 0 16.66%
  max-width: 16.66%
  padding: 0 14px

.group-link
  display: block
  min-height: 32px
  text-decoration: none
  line-height: 1.4
  color: #3b82f6
  font-size: 14px
  padding: 8px 10px
  border-radius: 6px
  transition: all 0.2s ease
  touch-action: manipulation
  -webkit-tap-highlight-color: transparent
  word-wrap: break-word
  word-break: break-word
  hyphens: auto
  overflow-wrap: break-word

  // Улучшенные настройки переноса для длинных названий
  white-space: normal
  text-align: left

  // Hover only for devices with mouse
  @media (hover: hover)
    &:hover
      background: rgba(59, 130, 246, 0.1)
      text-decoration: none
      color: #1e40af

  // Active state for touch devices
  &:active
    background: rgba(59, 130, 246, 0.15)
    transform: scale(0.98)

  &:not(:first-child)
    margin-top: 2px

.letter
  font-size: 18px
  font-weight: 700
  color: #64748b
  padding: 8px 8px 12px
  margin-bottom: 8px
  border-bottom: 2px solid #e2e8f0
  overflow: hidden
  text-overflow: ellipsis
  white-space: nowrap

// Responsive breakpoints - адаптируем для разного количества групп
@media (max-width: 1200px)
  .wide-cols .col, .medium-cols .col
    flex: 0 0 20%
    max-width: 20%

@media (max-width: 1100px)
  .wide-cols .col
    flex: 0 0 33.33%
    max-width: 33.33%

  .medium-cols .col
    flex: 0 0 20%
    max-width: 20%

@media (max-width: 900px)
  .wide-cols .col, .medium-cols .col
    flex: 0 0 33.33%
    max-width: 33.33%

@media (max-width: 800px)
  .col
    flex: 0 0 33.33%
    max-width: 33.33%

@media (max-width: 768px)
  .content-container
    padding: 0 16px

  .groups-title
    font-size: 1.25rem
    padding: 20px 20px 0

  .col
    flex: 0 0 33.33%
    max-width: 33.33%
    padding: 0 8px

@media (max-width: 600px)
  // Для мобилок всегда 1 колонка - переопределяем ВСЕ классы
  .col, .wide-cols .col, .medium-cols .col
    flex: 0 0 100% !important
    max-width: 100% !important
    padding: 0 12px

@media (max-width: 480px)
  .groups-page
    padding: 8px

  .content-container
    padding: 0 12px

  .groups-card
    border-radius: 8px

  .groups-title
    font-size: 1.1rem
    padding: 16px 16px 0

  .group-link
    font-size: 14px
    min-height: 44px
    line-height: 1.4
    padding: 12px 16px
    margin-bottom: 2px
    text-align: left

  .letter
    font-size: 16px
    padding: 8px 12px 12px
    text-align: left

  // Для мобилок всегда 1 колонка - переопределяем ВСЕ классы
  .col, .wide-cols .col, .medium-cols .col
    flex: 0 0 100% !important
    max-width: 100% !important
    padding: 0 8px

@media (max-width: 380px)
  .content-container
    padding: 0 8px

  .group-link
    font-size: 13px
    min-height: 46px
    padding: 14px 12px
    line-height: 1.3

  .letter
    font-size: 15px
    padding: 6px 8px 10px

  // На маленьких экранах всегда 1 колонка для удобства
  .col, .wide-cols .col, .medium-cols .col
    flex: 0 0 100% !important
    max-width: 100% !important
    padding: 0 4px

@media (max-width: 320px)
  .content-container
    padding: 0 6px

  .group-link
    font-size: 12px
    min-height: 48px
    padding: 16px 8px

  .letter
    font-size: 14px
    padding: 6px 6px 8px
</style>
